import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Navigate, Outlet, redirect, useRouterState } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { useSession } from './lib/session.tsx';
import { AppShell } from './components/app-shell.tsx';
import { isMonthKey } from './components/month-picker.tsx';
import { isIsoDate } from '@till-erp/platform/dates';
import { ARTICLE_LIST_TABS, type ArticleListTab, COMPONENT_PATTERN_PATH, INPUT_PATTERN_PATH, isTaskPageGroup, MASTER_DATA_TABS, type MasterDataTab, PLANS_PATH, RECORD_PATTERN_PATH, SETTINGS_PATTERN_PATH, SHOPWARE_IMPORT_PATH, SUGGESTIONS_PATH, type TaskPageGroup, DELIVERY_NOTE_FILTERS, type DeliveryNoteFilter, parseDeliveryNotePart } from './route-values.ts';
import {
  ARTICLE_IMPORT_PATH,
  GTIN_IMPORT_PATH,
  GTIN_NUMBERS_PATH,
  ARTICLE_NEW_PATH,
  CATALOG_MASTER_DATA_PATH,
  ARTICLES_PATH,
  AUTOMATION_PATH,
  BIN_LOCATIONS_PATH,
  CHANNEL_REVENUE_PATH,
  comingSoon,
  CUSTOMER_NEW_PATH,
  FINANCE_DATEV_PATH,
  FINANCE_DOCUMENTS_PATH,
  FINANCE_OPEN_ITEMS_PATH,
  FINANCE_PATH,
  FINANCE_PAYMENTS_PATH,
  CUSTOMERS_ANONYMIZE_PATH,
  CUSTOMERS_IMPORT_PATH,
  CUSTOMERS_PATH,
  DAILY_REVENUE_PATH,
  INVENTORY_IMPORT_PATH,
  INVENTORY_MOVEMENTS_PATH,
  INVENTORY_VALUATION_PATH,
  INVENTORY_PATH,
  MONTHLY_REVENUE_PATH,
  SOLD_POSITIONS_PATH,
  INCOMING_PAYMENTS_PATH,
  DATA_QUESTIONS_PATH,
  ORDER_DRAFTS_PATH,
  ORDER_BACKLOG_PATH,
  BLANKET_ORDER_NEW_PATH,
  BLANKET_ORDERS_PATH,
  ORDER_NEW_PATH,
  ORDERS_PATH,
  PURCHASING_PATH,
  QUOTE_NEW_PATH,
  QUOTES_PATH,
  REPORTS_PATH,
  TIME_AMENDMENTS_PATH,
  MONTH_CLOSE_PATH,
  TIME_TRACKING_PATH,
  MY_TIMES_PATH,
  STAFF_TEAM_PATH,
  WORKING_TIME_PATH,
  LEAVE_PATH,
  APPROVALS_PATH,
  CALENDAR_PATH,
  SUPPLIER_NEW_PATH,
  SUPPLIERS_IMPORT_PATH,
  SUPPLIERS_PATH,
  PURCHASE_ORDERS_PATH,
  REORDER_PROPOSALS_PATH,
  SUPPLIER_PRICES_IMPORT_PATH,
  SUPPLIER_PRICES_MASS_EDIT_PATH,
  SUPPLIERS_MASS_EDIT_PATH,
  TASKS_MASS_EDIT_PATH,
  SUPPLIER_PRICES_PATH,
  MARGIN_REVIEWS_PATH,
  PACKING_STATION_HOME,
  packingStationPathAllowed,
  staffOnlyPathAllowed,
  CHAT_PATH,
} from './navigation.ts';
import { PlaceholderPage } from './pages/placeholder.tsx';
import { PdfDropHost } from './components/pdf-drop-host.tsx';
import {
  AI_AGENTS_PATH,
  AI_BUDGET_PATH,
  AI_ARTICLE_ASSISTANT_PATH,
  MCP_KEYS_PATH,
  STAFF_EMPLOYEES_PATH,
  AI_PATH,
  COUNTERPARTS_PATH,
  CREDENTIALS_PATH,
  MAIL_PATH,
  MAIL_TEMPLATES_PATH,
  TASK_LABELS_PATH,
  LABELS_PATH,
  LABEL_PRINTERS_PATH,
  LABEL_PRINTS_PATH,
  TASK_REMINDER_PATH,
  TASK_RECURRENCE_PATH,
  TASK_TEMPLATES_PATH,
  OPERATIONS_PATH,
  ARCHIVE_BACKUP_PATH,
  DOCUMENT_TEMPLATES_PATH,
  EXAMPLE_INVOICES_PATH,
  NUMBER_RANGES_PATH,
  SHIPPING_COSTS_PATH,
  GTIN_SETTINGS_PATH,
  SHOPWARE_FIELD_COVERAGE_PATH,
  SHOPWARE_FIELDS_PATH,
  SWITCHES_PATH,
  EXCHANGE_RATES_PATH,
  TRIAL_PATH,
  COMPARISON_REPORT_PATH,
  CUTOVER_PATH,
  TAX_PATH,
  PRIVACY_PATH,
  ANONYMIZATIONS_PATH,
  SETTINGS_OVERVIEW_PATH,
  TEST_CLOCK_PATH,
  USERS_PATH,
} from './settings-pages.ts';

const PackingOnlyPage = lazy(() => import('./pages/packing-only.tsx').then((m) => ({ default: m.PackingOnlyPage })));
const StaffOnlyPage = lazy(() => import('./pages/staff-only.tsx').then((m) => ({ default: m.StaffOnlyPage })));
const StaffStartPage = lazy(() => import('./pages/staff-start.tsx').then((m) => ({ default: m.StaffStartPage })));

/**
 * Rolle Packplatz (Ticket 1.95): `/` führt auf das Versandzentrum; jede Seite außer Versand, Scannen und Profil zeigt
 * „Nur für den Versand“ – die eigentliche Seite wird gar nicht erst geladen (keine gesperrten Abfragen). Ohne Ablage
 * für PDF-Bestellungen. Der Server sperrt dieselben Routen (`apps/server/src/packing-station.ts`).
 */
function RootContent() {
  const { me } = useSession();
  // Pfad der gerade gezeigten Treffer (nicht `location`): Beim Wechsel steht `location` schon auf dem neuen Ziel, während
  // `<Outlet/>` noch die alte Seite zeigt – sonst rendert z. B. „Zum Versandzentrum“ kurz die Einstellungen, deren
  // Umleitung die Navigation überschreibt.
  const pathname = useRouterState({ select: (s) => s.matches.at(-1)?.pathname ?? s.location.pathname });
  // Rolle Mitarbeiter (Story 22.11): `/` ist die eigene Startseite (Stempel-Karte, Resturlaub, Woche); jede Seite außer
  // Start, Zeiterfassung (Heute, Meine Zeiten, Urlaub, Kalender) und Profil zeigt „Kein Zugriff“, ohne sie zu laden.
  if (me.role === 'mitarbeiter') {
    if (pathname === '/') {
      return (
        <Suspense fallback={null}>
          <StaffStartPage />
        </Suspense>
      );
    }
    if (!staffOnlyPathAllowed(pathname)) {
      return (
        <Suspense fallback={null}>
          <StaffOnlyPage />
        </Suspense>
      );
    }
    return <Outlet />;
  }
  if (me.role !== 'packplatz') {
    return (
      <>
        <Outlet />
        {/* Story 9.1: PDF-Bestellung auf jede Seite ziehen → Auftragsentwurf. */}
        <PdfDropHost />
      </>
    );
  }
  if (pathname === '/') return <Navigate to={PACKING_STATION_HOME} replace />;
  if (!packingStationPathAllowed(pathname)) {
    return (
      <Suspense fallback={null}>
        <PackingOnlyPage />
      </Suspense>
    );
  }
  return <Outlet />;
}

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <RootContent />
    </AppShell>
  ),
  notFoundComponent: () => <PlaceholderPage title="Seite nicht gefunden" />,
});

const startRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: lazyRouteComponent(() => import('./pages/start.tsx'), 'StartPage') });
// „Heute wichtig“ (Story 2.23): `?gruppe=approvals` zeigt nur diese Gruppe (Klärfälle: `cases`).
const tasksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/aufgaben',
  component: lazyRouteComponent(() => import('./pages/tasks.tsx'), 'TasksPage'),
  // Story 23.5: `?ansicht=liste` öffnet den Reiter „Liste“ (Listen-Baukasten); Story 23.7: `?ansicht=projekte` die Projekte.
  // Story 23.17: `?neu=1` öffnet die Neu-Maske („+ Neu › Aufgabe“ der Rolle Mitarbeiter).
  validateSearch: (search: Record<string, unknown>): { gruppe?: TaskPageGroup; ansicht?: 'liste' | 'projekte'; neu?: 1 } => ({
    ...(isTaskPageGroup(search.gruppe) ? { gruppe: search.gruppe } : {}),
    ...(search.ansicht === 'liste' || search.ansicht === 'projekte' ? { ansicht: search.ansicht } : {}),
    ...(search.neu === 1 || search.neu === '1' ? { neu: 1 as const } : {}),
  }),
});
// Story 23.5: Massenbearbeitung der Aufgabenliste und ihre Läufe.
const tasksMassEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: TASKS_MASS_EDIT_PATH,
  component: lazyRouteComponent(() => import('./pages/tasks-mass-edit.tsx'), 'TasksMassEditPage'),
});
const tasksMassEditRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/aufgaben/massenbearbeitung/$id',
  component: lazyRouteComponent(() => import('./pages/tasks-mass-edit.tsx'), 'TasksMassEditRunPage'),
});
const suggestionsRoute = createRoute({ getParentRoute: () => rootRoute, path: SUGGESTIONS_PATH, component: lazyRouteComponent(() => import('./pages/suggestions.tsx'), 'SuggestionsPage') });
const suggestionRoute = createRoute({ getParentRoute: () => rootRoute, path: '/ki-vorschlaege/$id', component: lazyRouteComponent(() => import('./pages/suggestions.tsx'), 'SuggestionPage') });
// Vorhaben der KI (Story 2.20).
const plansRoute = createRoute({ getParentRoute: () => rootRoute, path: PLANS_PATH, component: lazyRouteComponent(() => import('./pages/plans.tsx'), 'PlansPage') });
const planRoute = createRoute({ getParentRoute: () => rootRoute, path: '/vorhaben/$id', component: lazyRouteComponent(() => import('./pages/plans.tsx'), 'PlanPage') });
// Story 23.13: Link der Erinnerung in der Glocke – Aufgabenseite mit offenem Blatt der Aufgabe.
const taskRoute = createRoute({ getParentRoute: () => rootRoute, path: '/aufgaben/$taskId', component: lazyRouteComponent(() => import('./pages/tasks.tsx'), 'TasksPage') });
// Story 23.7: Projektseite (Ring, Liste/Board nur mit den Aufgaben des Projekts) – Aufgabenseite im Projekt-Modus.
const taskProjectRoute = createRoute({ getParentRoute: () => rootRoute, path: '/aufgaben/projekte/$projectId', component: lazyRouteComponent(() => import('./pages/tasks.tsx'), 'TasksPage') });
const caseRoute = createRoute({ getParentRoute: () => rootRoute, path: '/aufgaben/klaerfaelle/$id', component: lazyRouteComponent(() => import('./pages/case.tsx'), 'CasePage') });
const settingsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/einstellungen', component: lazyRouteComponent(() => import('./pages/settings-overview.tsx'), 'SettingsIndexPage') });
// Einstellungen „Auf einen Blick“ (Ticket 1.209): feste Startseite, gewinnt gegen `/einstellungen/$bereich`.
const settingsOverviewRoute = createRoute({ getParentRoute: () => rootRoute, path: SETTINGS_OVERVIEW_PATH, component: lazyRouteComponent(() => import('./pages/settings-overview.tsx'), 'SettingsOverviewPage') });
// Einstellungen (Story 1.41): feste Seiten unter `/einstellungen/<hauptpunkt>/<reiter>` gewinnen gegen
// `/einstellungen/$bereich/$reiter` (Reiter aus Gruppen); `/einstellungen/$bereich` öffnet den ersten Reiter bzw. leitet
// alte Adressen um.
const counterpartsRoute = createRoute({ getParentRoute: () => rootRoute, path: COUNTERPARTS_PATH, component: lazyRouteComponent(() => import('./pages/connections.tsx'), 'ConnectionsPage') });
const exampleInvoiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `${EXAMPLE_INVOICES_PATH}/$id`,
  component: lazyRouteComponent(() => import('./pages/example-invoice.tsx'), 'ExampleInvoicePage'),
});
const switchesRoute = createRoute({ getParentRoute: () => rootRoute, path: SWITCHES_PATH, component: lazyRouteComponent(() => import('./pages/connections.tsx'), 'ConnectionsPage') });
const exchangeRatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: EXCHANGE_RATES_PATH,
  component: lazyRouteComponent(() => import('./pages/exchange-rates.tsx'), 'ExchangeRatesPage'),
});
const trialRoute = createRoute({ getParentRoute: () => rootRoute, path: TRIAL_PATH, component: lazyRouteComponent(() => import('./pages/trial.tsx'), 'TrialPage') });
const comparisonReportRoute = createRoute({ getParentRoute: () => rootRoute, path: COMPARISON_REPORT_PATH, component: lazyRouteComponent(() => import('./pages/comparison-report.tsx'), 'ComparisonReportPage') });
const cutoverRoute = createRoute({ getParentRoute: () => rootRoute, path: CUTOVER_PATH, component: lazyRouteComponent(() => import('./pages/cutover.tsx'), 'CutoverPage') });
const aiRoute = createRoute({ getParentRoute: () => rootRoute, path: AI_PATH, component: lazyRouteComponent(() => import('./pages/ai.tsx'), 'AiPage') });
const aiAgentsRoute = createRoute({ getParentRoute: () => rootRoute, path: AI_AGENTS_PATH, component: lazyRouteComponent(() => import('./pages/ai-agents.tsx'), 'AiAgentsPage') });
const aiBudgetRoute = createRoute({ getParentRoute: () => rootRoute, path: AI_BUDGET_PATH, component: lazyRouteComponent(() => import('./pages/ai-budget.tsx'), 'AiBudgetPage') });
// Story 5.102: Einstellungen › KI › Artikel-Assistent (nur Admins).
const aiArticleAssistantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AI_ARTICLE_ASSISTANT_PATH,
  component: lazyRouteComponent(() => import('./pages/ai-article-assistant.tsx'), 'AiArticleAssistantPage'),
});
const mcpKeysRoute = createRoute({ getParentRoute: () => rootRoute, path: MCP_KEYS_PATH, component: lazyRouteComponent(() => import('./pages/mcp-keys.tsx'), 'McpKeysPage') });
const credentialsRoute = createRoute({ getParentRoute: () => rootRoute, path: CREDENTIALS_PATH, component: lazyRouteComponent(() => import('./pages/connections.tsx'), 'ConnectionsPage') });
const taxRoute = createRoute({ getParentRoute: () => rootRoute, path: TAX_PATH, component: lazyRouteComponent(() => import('./pages/tax.tsx'), 'TaxPage') });
const mailTemplatesRoute = createRoute({ getParentRoute: () => rootRoute, path: MAIL_TEMPLATES_PATH, component: lazyRouteComponent(() => import('./pages/mail-templates.tsx'), 'MailTemplatesPage') });
// Story 23.4: Einstellungen › Aufgaben › Labels (alle sehen, ändern nur Admin).
const taskLabelsRoute = createRoute({ getParentRoute: () => rootRoute, path: TASK_LABELS_PATH, component: lazyRouteComponent(() => import('./pages/task-labels.tsx'), 'TaskLabelsPage') });
// Story 23.13: Einstellungen › Aufgaben › Erinnerung (nur Admin).
const taskReminderRoute = createRoute({ getParentRoute: () => rootRoute, path: TASK_REMINDER_PATH, component: lazyRouteComponent(() => import('./pages/task-reminder-settings.tsx'), 'TaskReminderSettingsPage') });
// Story 23.11: Einstellungen › Aufgaben › Wiederholungen (nur Admin).
const taskRecurrenceRoute = createRoute({ getParentRoute: () => rootRoute, path: TASK_RECURRENCE_PATH, component: lazyRouteComponent(() => import('./pages/task-recurrence-settings.tsx'), 'TaskRecurrenceSettingsPage') });
// Story 23.12: Einstellungen › Aufgaben › Vorlagen (nur Admin).
const taskTemplatesRoute = createRoute({ getParentRoute: () => rootRoute, path: TASK_TEMPLATES_PATH, component: lazyRouteComponent(() => import('./pages/task-templates.tsx'), 'TaskTemplatesPage') });
// Story 27.5: Einstellungen › Drucken › Etiketten (Galerie) und der Editor einer Vorlage (Vollbild-Werkbank).
const labelsRoute = createRoute({ getParentRoute: () => rootRoute, path: LABELS_PATH, component: lazyRouteComponent(() => import('./pages/labels.tsx'), 'LabelsPage') });
// Story 27.6: Einstellungen › Drucken › Drucker (Profile, Testdruck) und Druckverlauf.
const labelPrintersRoute = createRoute({ getParentRoute: () => rootRoute, path: LABEL_PRINTERS_PATH, component: lazyRouteComponent(() => import('./pages/label-printers.tsx'), 'LabelPrintersPage') });
const labelPrintsRoute = createRoute({ getParentRoute: () => rootRoute, path: LABEL_PRINTS_PATH, component: lazyRouteComponent(() => import('./pages/label-prints.tsx'), 'LabelPrintsPage') });
const labelEditorRoute = createRoute({ getParentRoute: () => rootRoute, path: `${LABELS_PATH}/$id`, component: lazyRouteComponent(() => import('./pages/label-editor.tsx'), 'LabelEditorPage') });
const operationsRoute = createRoute({ getParentRoute: () => rootRoute, path: OPERATIONS_PATH, component: lazyRouteComponent(() => import('./pages/operations.tsx'), 'OperationsPage') });
// Ticket 1.268: Technik › Archiv und Sicherung (Zustand und Grenzen; vorher Zeile und Felder unter Betrieb).
const archiveBackupRoute = createRoute({ getParentRoute: () => rootRoute, path: ARCHIVE_BACKUP_PATH, component: lazyRouteComponent(() => import('./pages/archive-backup.tsx'), 'ArchiveBackupPage') });
// Story 25.1: Einstellungen › Lager & Versand › EAN-Nummern (Dubletten-Bericht).
const gtinSettingsRoute = createRoute({ getParentRoute: () => rootRoute, path: GTIN_SETTINGS_PATH, component: lazyRouteComponent(() => import('./pages/gtin-settings.tsx'), 'GtinSettingsPage') });
const shippingCostsRoute = createRoute({ getParentRoute: () => rootRoute, path: SHIPPING_COSTS_PATH, component: lazyRouteComponent(() => import('./pages/shipping-costs.tsx'), 'ShippingCostsPage') });
const shopwareFieldsRoute = createRoute({ getParentRoute: () => rootRoute, path: SHOPWARE_FIELDS_PATH, component: lazyRouteComponent(() => import('./pages/shopware-fields.tsx'), 'ShopwareFieldsPage') });
// Feld-Abdeckung (Story 5.29), optional mit Bereich (Ziel der Aufgabe „Neue Shopware-Felder entscheiden“).
const shopwareFieldCoverageRoute = createRoute({ getParentRoute: () => rootRoute, path: SHOPWARE_FIELD_COVERAGE_PATH, component: lazyRouteComponent(() => import('./pages/shopware-field-coverage.tsx'), 'ShopwareFieldCoveragePage') });
const shopwareFieldCoverageAreaRoute = createRoute({ getParentRoute: () => rootRoute, path: `${SHOPWARE_FIELD_COVERAGE_PATH}/$bereich`, component: lazyRouteComponent(() => import('./pages/shopware-field-coverage.tsx'), 'ShopwareFieldCoveragePage') });
const documentTemplatesRoute = createRoute({ getParentRoute: () => rootRoute, path: DOCUMENT_TEMPLATES_PATH, component: lazyRouteComponent(() => import('./pages/document-templates.tsx'), 'DocumentTemplatesPage') });
// Beleg-Ansicht mit allen Fassungen und Chatter (Story 3.5).
const documentRoute = createRoute({ getParentRoute: () => rootRoute, path: '/belege/$documentId', component: lazyRouteComponent(() => import('./pages/document.tsx'), 'DocumentPage') });
// Datenschutz › Personendaten (Story 18.2).
const privacyRoute = createRoute({ getParentRoute: () => rootRoute, path: PRIVACY_PATH, component: lazyRouteComponent(() => import('./pages/privacy.tsx'), 'PrivacyPage') });
const anonymizationsRoute = createRoute({ getParentRoute: () => rootRoute, path: ANONYMIZATIONS_PATH, component: lazyRouteComponent(() => import('./pages/privacy-anonymizations.tsx'), 'PrivacyAnonymizationsPage') });
const numberRangesRoute = createRoute({ getParentRoute: () => rootRoute, path: NUMBER_RANGES_PATH, component: lazyRouteComponent(() => import('./pages/number-ranges.tsx'), 'NumberRangesPage') });
// Gruppe „E-Mail“ hat eine eigene Seite (Zustand, Versandliste, darunter die Einstellungen der Gruppe).
const mailRoute = createRoute({ getParentRoute: () => rootRoute, path: MAIL_PATH, component: lazyRouteComponent(() => import('./pages/mail.tsx'), 'MailPage') });
const settingsSectionRoute = createRoute({ getParentRoute: () => rootRoute, path: '/einstellungen/$bereich', component: lazyRouteComponent(() => import('./pages/settings.tsx'), 'SettingsSectionPage') });
const settingsTabRoute = createRoute({ getParentRoute: () => rootRoute, path: '/einstellungen/$bereich/$reiter', component: lazyRouteComponent(() => import('./pages/settings.tsx'), 'SettingsTabPage') });
const usersRoute = createRoute({ getParentRoute: () => rootRoute, path: USERS_PATH, component: lazyRouteComponent(() => import('./pages/users.tsx'), 'UsersPage') });
const userDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: `${USERS_PATH}/$id`, component: lazyRouteComponent(() => import('./pages/users.tsx'), 'UserDetailPage') });
const testClockRoute = createRoute({ getParentRoute: () => rootRoute, path: TEST_CLOCK_PATH, component: lazyRouteComponent(() => import('./pages/test-clock.tsx'), 'TestClockPage') });
// Aufträge: Liste und Vollansicht (epic-auftragseingang Story 1).
// Kacheln „Versandbereit“ / „Klärfälle“ der Startseite: `?ampel=ready|case` belegt den Ampel-Filter vor (unbekannte Werte ignoriert);
// `?ampel=check` (Hinweis-Karte „Aufträge prüfen“, Ticket 1.275): Ampel „Prüfen“ und „Wartet“.
const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ORDERS_PATH,
  component: lazyRouteComponent(() => import('./pages/orders.tsx'), 'OrdersPage'),
  staticData: { list: 'orders' },
  // Ticket 1.272: Kachel „Heute eingegangen“ über der Liste belegt mit `?heute=ja` den Filter „Bestellt heute“ vor.
  validateSearch: (search: Record<string, unknown>): { ampel?: 'ready' | 'case' | 'check'; heute?: 'ja' } => ({
    ...(search.ampel === 'ready' || search.ampel === 'case' || search.ampel === 'check' ? { ampel: search.ampel } : {}),
    ...(search.heute === 'ja' ? { heute: 'ja' as const } : {}),
  }),
});
const orderDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: '/auftraege/$id', component: lazyRouteComponent(() => import('./pages/orders.tsx'), 'OrderDetailPage') });
// Story 9.26: Auftrag bearbeiten bis Versand (Positionen, Mengen, Preise, Rabatte, Versand- und Zahlart).
// Story 9.34: eine Maske mit vier Karten; `?karte=lieferung` (Ampel „Lieferung bearbeiten“) setzt den Fokus in die Karte Lieferung.
const orderEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auftraege/$id/bearbeiten',
  component: lazyRouteComponent(() => import('./pages/order-edit-lines.tsx'), 'OrderEditLinesPage'),
  validateSearch: (search: Record<string, unknown>): { karte?: 'lieferung' } => (search.karte === 'lieferung' ? { karte: 'lieferung' } : {}),
});
// Story 9.1: Auftragsentwürfe mit „Auftrag aus PDF“ (fester Pfad geht vor `/auftraege/$id`).
const orderDraftsRoute = createRoute({ getParentRoute: () => rootRoute, path: ORDER_DRAFTS_PATH, component: lazyRouteComponent(() => import('./pages/order-drafts.tsx'), 'OrderDraftsPage') });
// Story 7.16: Liste „Wartet auf Ware“ (alle Positionen im Rückstand).
const orderBacklogRoute = createRoute({ getParentRoute: () => rootRoute, path: ORDER_BACKLOG_PATH, component: lazyRouteComponent(() => import('./pages/order-backlog.tsx'), 'OrderBacklogPage') });
// Story 9.24: Rahmenaufträge – Liste, Anlage-Maske (`kunde` = vorgewählter Kunde) und Vollansicht (feste Pfade vor
// `/auftraege/$id`; `neu` vor `$id` der Rahmenaufträge).
const uuidSearch = (v: unknown) => (typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v) ? v.toLowerCase() : undefined);
const blanketOrdersRoute = createRoute({ getParentRoute: () => rootRoute, path: BLANKET_ORDERS_PATH, component: lazyRouteComponent(() => import('./pages/blanket-orders.tsx'), 'BlanketOrdersPage') });
const blanketOrderNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: BLANKET_ORDER_NEW_PATH,
  component: lazyRouteComponent(() => import('./pages/blanket-orders.tsx'), 'BlanketOrderNewPage'),
  validateSearch: (search: Record<string, unknown>): { kunde?: string } => {
    const kunde = uuidSearch(search.kunde);
    return kunde ? { kunde } : {};
  },
});
const blanketOrderDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: `${BLANKET_ORDERS_PATH}/$id`, component: lazyRouteComponent(() => import('./pages/blanket-orders.tsx'), 'BlanketOrderDetailPage') });
// Story 7.17: Meldung zum Wareneingang („N Aufträge können jetzt raus“), Ziel des Glocken-Hinweises.
const receiptNoticeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/auftraege/wareneingang/$id', component: lazyRouteComponent(() => import('./pages/receipt-notice.tsx'), 'ReceiptNoticePage') });
// Story 9.5: „+ Neu › Auftrag“ – Maske „Auftrag anlegen“ (fester Pfad geht vor `/auftraege/$id`); `kunde` = vorgewählter
// Kunde („Auftrag für diesen Kunden“, Rückkehr aus „Neuen Kunden anlegen“), `entwurf` = gespeicherter Entwurf.
const orderNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ORDER_NEW_PATH,
  component: lazyRouteComponent(() => import('./pages/order-new.tsx'), 'OrderNewPage'),
  // Story 9.4: `pdf` = Auftragsentwurf aus PDF prüfen und freigeben, `ablage` = Ablage „Bestellung einlesen“ oben.
  // Story 9.23: `erneut` = „Erneut bestellen“ aus diesem Auftrag (Preise von heute).
  // Story 9.24: `abruf` = Abruf aus diesem Rahmenauftrag, `mengen` = „<Rahmenposition>:<Menge>,…“ aus dem Blatt.
  validateSearch: (search: Record<string, unknown>): { kunde?: string; entwurf?: string; pdf?: string; ablage?: 1; erneut?: string; abruf?: string; mengen?: string } => {
    const uuid = (v: unknown) => (typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v) ? v.toLowerCase() : undefined);
    const kunde = uuid(search.kunde);
    const entwurf = uuid(search.entwurf);
    const pdf = uuid(search.pdf);
    const ablage = search.ablage === '1' || search.ablage === 1 || search.ablage === true;
    const erneut = uuid(search.erneut);
    const abruf = uuid(search.abruf);
    const mengen = typeof search.mengen === 'string' && /^[0-9a-f:,-]{1,4000}$/i.test(search.mengen) ? search.mengen.toLowerCase() : undefined;
    return {
      ...(kunde ? { kunde } : {}),
      ...(entwurf ? { entwurf } : {}),
      ...(pdf ? { pdf } : {}),
      ...(ablage && !pdf ? { ablage: 1 as const } : {}),
      ...(erneut ? { erneut } : {}),
      ...(abruf ? { abruf } : {}),
      ...(abruf && mengen ? { mengen } : {}),
    };
  },
});
// Story 9.7: Angebote – Liste (`angelegt` = Nummer des eben gespeicherten Angebots) und Maske „Angebot anlegen“.
const quotesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: QUOTES_PATH,
  component: lazyRouteComponent(() => import('./pages/quotes.tsx'), 'QuotesPage'),
  staticData: { list: 'quotes' },
  validateSearch: (search: Record<string, unknown>): { angelegt?: string } =>
    typeof search.angelegt === 'string' && /^[0-9A-Za-z-]{1,30}$/.test(search.angelegt) ? { angelegt: search.angelegt } : {},
});
// Story 9.22: `ablage` = Ablage „Angebot aus Anfrage“ oben, `anfrage` = Angebotsentwurf aus einer Anfrage prüfen und speichern.
const quoteNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: QUOTE_NEW_PATH,
  component: lazyRouteComponent(() => import('./pages/order-new.tsx'), 'QuoteNewPage'),
  validateSearch: (search: Record<string, unknown>): { ablage?: 1; anfrage?: string } => {
    const anfrage = typeof search.anfrage === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(search.anfrage) ? search.anfrage.toLowerCase() : undefined;
    const ablage = search.ablage === '1' || search.ablage === 1 || search.ablage === true;
    return { ...(anfrage ? { anfrage } : {}), ...(ablage && !anfrage ? { ablage: 1 as const } : {}) };
  },
});
// Story 9.8: Angebotsseite (Reiter Positionen/Belege; `gespeichert` = Fassung nach „Speichern“, 0 = unverändert, -1 = ohne
// neue Fassung) und „Angebot bearbeiten“. `/angebote/neu` ist ein fester Pfad und gewinnt gegen `$quoteId`.
const quoteDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/angebote/$quoteId',
  component: lazyRouteComponent(() => import('./pages/quote-detail.tsx'), 'QuoteDetailPage'),
  validateSearch: (search: Record<string, unknown>): { reiter?: 'belege'; gespeichert?: number } => {
    const g = typeof search.gespeichert === 'number' ? search.gespeichert : typeof search.gespeichert === 'string' ? Number(search.gespeichert) : Number.NaN;
    return {
      ...(search.reiter === 'belege' ? { reiter: 'belege' as const } : {}),
      ...(Number.isInteger(g) && g >= -1 && g < 10000 ? { gespeichert: g } : {}),
    };
  },
});
const quoteEditRoute = createRoute({ getParentRoute: () => rootRoute, path: '/angebote/$quoteId/bearbeiten', component: lazyRouteComponent(() => import('./pages/order-new.tsx'), 'QuoteEditPage') });
const customersRoute = createRoute({ getParentRoute: () => rootRoute, path: CUSTOMERS_PATH, component: lazyRouteComponent(() => import('./pages/customers.tsx'), 'CustomersPage'), staticData: { list: 'customers' } });
// Feste Seiten vor dem Kunden: `/kunden/massenbearbeitung` gewinnt gegen `/kunden/$id`. Massenbearbeitung nachgeladen
// (AG Grid nur dort, Story 1.21).
const customerMassEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kunden/massenbearbeitung',
  component: lazyRouteComponent(() => import('./pages/customer-mass-edit.tsx'), 'CustomerMassEditPage'),
});
const customerMassEditRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kunden/massenbearbeitung/$id',
  component: lazyRouteComponent(() => import('./pages/customer-mass-edit.tsx'), 'CustomerMassEditRunPage'),
});
// Story 18.10: Kunden › Import/Export (Konditionen; fester Pfad geht vor `/kunden/$id`).
const customerImportRoute = createRoute({ getParentRoute: () => rootRoute, path: CUSTOMERS_IMPORT_PATH, component: lazyRouteComponent(() => import('./pages/list-imports.tsx'), 'CustomerImportPage') });
// Story 18.4: ausgewählte Kunden anonymisieren (fester Pfad geht vor `/kunden/$id`).
const customersAnonymizeRoute = createRoute({ getParentRoute: () => rootRoute, path: CUSTOMERS_ANONYMIZE_PATH, component: lazyRouteComponent(() => import('./pages/customer-anonymize.tsx'), 'CustomersAnonymizePage') });
// Story 4.20: „+ Neu › Kunde“ (fester Pfad geht vor `/kunden/$id`).
// Story 9.5: `zurueck=auftrag` – nach dem Anlegen zurück in „Auftrag anlegen“ mit dem neuen Kunden.
const customerNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CUSTOMER_NEW_PATH,
  component: lazyRouteComponent(() => import('./pages/customer-new.tsx'), 'CustomerNewPage'),
  validateSearch: (search: Record<string, unknown>): { zurueck?: 'auftrag' } => (search.zurueck === 'auftrag' ? { zurueck: 'auftrag' } : {}),
});
// Lauf ohne Liste im Pfad (Story 1.31: Glocke, Aufgabe) → Seite des Laufs in seiner Liste.
const massEditRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/massenbearbeitungen/$id',
  component: lazyRouteComponent(() => import('./pages/mass-edit-redirect.tsx'), 'MassEditRedirectPage'),
});
// Hinweis „2 neue Artikel in ‚Bald leer‘“ aus der Glocke (Story 1.34) → Liste mit der Ansicht und den neuen Zeilen markiert.
const watchHitRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/beobachtet/$id',
  component: lazyRouteComponent(() => import('./pages/watch-hit-redirect.tsx'), 'WatchHitRedirectPage'),
});
// „Neu im ERP“ (Story 1.99): alle Neuerungen, über die Glocke („Alle Neuigkeiten“).
const releaseNotesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/neu-im-erp',
  component: lazyRouteComponent(() => import('./pages/release-notes.tsx'), 'ReleaseNotesPage'),
});
// `?zusammengefuehrt=20120`: Weiterleitung von einem zusammengeführten Kunden (Story 4.11) – Hinweis oben in der Ansicht.
const customerDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kunden/$id',
  component: lazyRouteComponent(() => import('./pages/customers.tsx'), 'CustomerDetailPage'),
  // Als Zahl in der Adresse (`?zusammengefuehrt=20120`, ohne Anführungszeichen); gelesen als 5-stellige Kundennummer.
  // `?reiter=adressen` öffnet den Reiter „Adressen“ (Story 4.25, Weiterleitung aus dem Klärfall).
  // Ticket 1.274 Teil 2: `angelegt=1` nach „Anlegen“ zeigt einmal „Angelegt – bereit für Angebot und Auftrag.“ (S8).
  validateSearch: (search: Record<string, unknown>): { zusammengefuehrt?: number; reiter?: 'adressen'; angelegt?: 1 } => {
    const value = Number(search.zusammengefuehrt);
    return {
      ...(Number.isInteger(value) && value > 0 && value <= 99_999 ? { zusammengefuehrt: value } : {}),
      ...(search.reiter === 'adressen' ? { reiter: 'adressen' as const } : {}),
      ...(search.angelegt === 1 || search.angelegt === '1' ? { angelegt: 1 as const } : {}),
    };
  },
});
// Adresse des Adressbuchs (Story 4.25: Klärfall „Adresse nicht nach Shopware übertragen“) → Kunde, Reiter „Adressen“.
const customerAddressRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kunden/adresse/$id',
  component: lazyRouteComponent(() => import('./pages/customer-address-redirect.tsx'), 'CustomerAddressRedirectPage'),
});
// Story 5.100: `?reiter=ausgeblendet` wählt den Reiter „Ausgeblendet“; unbekannter Wert → „Artikel“.
const articlesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ARTICLES_PATH,
  component: lazyRouteComponent(() => import('./pages/articles.tsx'), 'ArticlesPage'),
  staticData: { list: 'articles' },
  validateSearch: (search: Record<string, unknown>): { reiter?: ArticleListTab; ohneEan?: 1 } => ({
    ...(typeof search.reiter === 'string' && (ARTICLE_LIST_TABS as readonly string[]).includes(search.reiter) ? { reiter: search.reiter as ArticleListTab } : {}),
    ...(search.ohneEan === 1 || search.ohneEan === '1' ? { ohneEan: 1 as const } : {}),
  }),
});
// Ticket 1.273: `angelegt=1` nach „Anlegen“ zeigt einmal die Bestätigung „Artikel TT-… angelegt“ (Freude-Moment, S8).
const articleDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/artikel/$id',
  component: lazyRouteComponent(() => import('./pages/articles.tsx'), 'ArticleDetailPage'),
  // Story 5.107: `bilder=n` = n vorgemerkte Bilder konnten nach dem Anlegen nicht hochgeladen werden (Hinweis in der Bestätigung).
  // Story 12.20: `reiter=versand` öffnet den Reiter „Versand & Zoll“ (Sprung aus der Rechnungs-Vorschau); sonst „Stammdaten“.
  validateSearch: (search: Record<string, unknown>): { angelegt?: 1; bilder?: number; reiter?: 'versand' | 'lager' } => {
    const bilder = Number(search.bilder);
    return {
      ...(search.angelegt === 1 || search.angelegt === '1' ? { angelegt: 1 as const } : {}),
      ...(Number.isInteger(bilder) && bilder > 0 && bilder < 100 ? { bilder } : {}),
      ...(search.reiter === 'versand' || search.reiter === 'lager' ? { reiter: search.reiter } : {}),
    };
  },
});
// Story 5.12: Massenbearbeitung der Artikelliste (fester Pfad geht vor `/artikel/$id`), nachgeladen wie bei den Kunden.
const articleMassEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/artikel/massenbearbeitung',
  component: lazyRouteComponent(() => import('./pages/article-mass-edit.tsx'), 'ArticleMassEditPage'),
});
const articleMassEditRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/artikel/massenbearbeitung/$id',
  component: lazyRouteComponent(() => import('./pages/article-mass-edit.tsx'), 'ArticleMassEditRunPage'),
});
// Story 18.9: Artikel › Import/Export (fester Pfad geht vor `/artikel/$id`).
const articleImportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ARTICLE_IMPORT_PATH,
  component: lazyRouteComponent(() => import('./pages/article-import.tsx'), 'ArticleImportPage'),
});
// Story 25.2: Artikel › EAN-Nummern und „Nummern einlesen“ (feste Pfade gehen vor `/artikel/$id`).
const gtinNumbersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: GTIN_NUMBERS_PATH,
  component: lazyRouteComponent(() => import('./pages/gtin-numbers.tsx'), 'GtinNumbersPage'),
  validateSearch: (search: Record<string, unknown>): { charge?: string } => typeof search.charge === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(search.charge) ? { charge: search.charge } : {},
});
const gtinImportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: GTIN_IMPORT_PATH,
  component: lazyRouteComponent(() => import('./pages/gtin-import.tsx'), 'GtinImportPage'),
});
// Story 5.8: „+ Neu › Artikel“ (fester Pfad geht vor `/artikel/$id`).
const articleNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ARTICLE_NEW_PATH,
  // Story 5.120: `entwurf` = offener KI-Entwurf der Person (Link aus dem Erlebnis-Review); unbekannt/geschlossen → leere Seite.
  validateSearch: (search: Record<string, unknown>): { entwurf?: string } =>
    typeof search.entwurf === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(search.entwurf) ? { entwurf: search.entwurf.toLowerCase() } : {},
  component: lazyRouteComponent(() => import('./pages/article-form.tsx'), 'ArticleNewPage'),
});
// Story 5.9: Artikel › Stammdaten (Hersteller · Eigenschaften · Kategorien); `?reiter=` wählt den Reiter.
const catalogMasterDataRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CATALOG_MASTER_DATA_PATH,
  component: lazyRouteComponent(() => import('./pages/catalog-master-data.tsx'), 'CatalogMasterDataPage'),
  validateSearch: (search: Record<string, unknown>): { reiter?: MasterDataTab } =>
    typeof search.reiter === 'string' && (MASTER_DATA_TABS as readonly string[]).includes(search.reiter) ? { reiter: search.reiter as MasterDataTab } : {},
});
const inventoryRoute = createRoute({ getParentRoute: () => rootRoute, path: INVENTORY_PATH, component: lazyRouteComponent(() => import('./pages/inventory.tsx'), 'InventoryPage') });
// Bestandsbewegungen (Story 5.38): `?artikel=TT-GB-M8` belegt den Filter „Artikelnummer ist …“ vor.
const inventoryMovementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: INVENTORY_MOVEMENTS_PATH,
  component: lazyRouteComponent(() => import('./pages/inventory.tsx'), 'MovementsPage'),
  // Runde 2 (Story 26.11): `?ansicht=tabelle` zeigt die Liste mit allen Spalten statt der Warenkörbe je Lieferant.
  validateSearch: (search: Record<string, unknown>): { artikel?: string; ansicht?: 'tabelle' } => ({
    ...(typeof search.artikel === 'string' && search.artikel.trim() !== '' ? { artikel: search.artikel.trim() } : {}),
    ...(search.ansicht === 'tabelle' ? { ansicht: 'tabelle' as const } : {}),
  }),
});
// Lagerwert zum Stichtag (Story 5.44): `?stichtag=2027-02-14` (gültiges ISO-Datum, sonst ohne – dann heute).
const inventoryValuationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: INVENTORY_VALUATION_PATH,
  component: lazyRouteComponent(() => import('./pages/inventory.tsx'), 'StockValuationPage'),
  validateSearch: (search: Record<string, unknown>): { stichtag?: string } =>
    typeof search.stichtag === 'string' && isIsoDate(search.stichtag) ? { stichtag: search.stichtag } : {},
});
const binLocationsRoute = createRoute({ getParentRoute: () => rootRoute, path: BIN_LOCATIONS_PATH, component: lazyRouteComponent(() => import('./pages/inventory.tsx'), 'BinLocationsPage') });
// Massenbearbeitung der Lagerplätze (Story 5.39).
const binLocationsMassEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lager/lagerplaetze/massenbearbeitung',
  component: lazyRouteComponent(() => import('./pages/inventory-mass-edit.tsx'), 'BinLocationsMassEditPage'),
});
const binLocationsMassEditRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lager/lagerplaetze/massenbearbeitung/$id',
  component: lazyRouteComponent(() => import('./pages/inventory-mass-edit.tsx'), 'BinLocationsMassEditRunPage'),
});
// Massenbearbeitung der Lagerliste (Story 5.37), nachgeladen wie bei den Kunden.
const inventoryMassEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lager/massenbearbeitung',
  component: lazyRouteComponent(() => import('./pages/inventory-mass-edit.tsx'), 'InventoryMassEditPage'),
});
const inventoryMassEditRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lager/massenbearbeitung/$id',
  component: lazyRouteComponent(() => import('./pages/inventory-mass-edit.tsx'), 'InventoryMassEditRunPage'),
});
// Story 18.10: Lager › Import/Export (Lagerplätze).
const inventoryImportRoute = createRoute({ getParentRoute: () => rootRoute, path: INVENTORY_IMPORT_PATH, component: lazyRouteComponent(() => import('./pages/list-imports.tsx'), 'InventoryImportPage') });
// Inventur (Story 5.40): Liste als Reiter unter „Lager“, neue Inventur und Zählliste je Inventur (nachgeladen).
const stocktakesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/lager/inventuren', component: lazyRouteComponent(() => import('./pages/inventory.tsx'), 'StocktakesPage') });
const stocktakeNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lager/inventuren/neu',
  component: lazyRouteComponent(() => import('./pages/stocktakes.tsx'), 'NewStocktakePage'),
});
const stocktakeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lager/inventuren/$id',
  component: lazyRouteComponent(() => import('./pages/stocktakes.tsx'), 'StocktakePage'),
});
// Druckansicht Zählliste / Protokoll (Story 5.41).
const stocktakePrintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lager/inventuren/$id/druck/$art',
  component: lazyRouteComponent(() => import('./pages/stocktake-print.tsx'), 'StocktakePrintPage'),
});
const recountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lager/nachzaehlen',
  component: lazyRouteComponent(() => import('./pages/recounts.tsx'), 'RecountsPage'),
});
// Berichte (Story 18.1, 18.6): Übersicht, Tagesumsatz, Monatsumsatz und Umsatz nach Kanal.
// Einkauf (Story 10.1, Ticket 33): „Einkauf“ (Seitenleiste, Handy-Menü, Strg K) öffnet die Bestellvorschläge –
// Einstieg = was zu tun ist (A8: Bestellvorschlag in 3 Klicks übernehmen). Lieferanten bleiben unter `/einkauf/lieferanten`.
const purchasingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: PURCHASING_PATH,
  beforeLoad: () => {
    throw redirect({ to: REORDER_PROPOSALS_PATH, replace: true });
  },
});
const suppliersRoute = createRoute({ getParentRoute: () => rootRoute, path: SUPPLIERS_PATH, component: lazyRouteComponent(() => import('./pages/purchasing.tsx'), 'SuppliersPage') });
// Story 18.17: Einkauf › Lieferanten › Import/Export (Lieferantenstamm).
const suppliersImportRoute = createRoute({ getParentRoute: () => rootRoute, path: SUPPLIERS_IMPORT_PATH, component: lazyRouteComponent(() => import('./pages/list-imports.tsx'), 'SupplierImportPage') });
const supplierNewRoute = createRoute({ getParentRoute: () => rootRoute, path: SUPPLIER_NEW_PATH, component: lazyRouteComponent(() => import('./pages/purchasing.tsx'), 'SupplierNewPage') });
const supplierDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: '/einkauf/lieferanten/$id', component: lazyRouteComponent(() => import('./pages/purchasing.tsx'), 'SupplierDetailPage') });
// Einkauf › Bestellungen (Story 10.3).
const purchaseOrdersRoute = createRoute({ getParentRoute: () => rootRoute, path: PURCHASE_ORDERS_PATH, component: lazyRouteComponent(() => import('./pages/purchase-orders.tsx'), 'PurchaseOrdersPage') });
const purchaseOrderDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: '/einkauf/bestellungen/$id', component: lazyRouteComponent(() => import('./pages/purchase-orders.tsx'), 'PurchaseOrderDetailPage') });
// Einkauf › Bestellvorschläge (Story 10.6).
// `?artikel=` (Ticket 45): Sprung „Bestellvorschlag öffnen“ aus der Lagerliste belegt „Artikelnummer ist …“ vor.
const reorderProposalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: REORDER_PROPOSALS_PATH,
  component: lazyRouteComponent(() => import('./pages/reorder-proposals.tsx'), 'ReorderProposalsPage'),
  // Runde 2 (Story 26.11): `?ansicht=tabelle` zeigt die Liste mit allen Spalten statt der Warenkörbe je Lieferant.
  validateSearch: (search: Record<string, unknown>): { artikel?: string; ansicht?: 'tabelle' } => ({
    ...(typeof search.artikel === 'string' && search.artikel.trim() !== '' ? { artikel: search.artikel.trim() } : {}),
    ...(search.ansicht === 'tabelle' ? { ansicht: 'tabelle' as const } : {}),
  }),
});
// Story 18.10: Einkauf › Lieferantenpreise (Liste), Import/Export und Läufe (Verlauf, ein Lauf mit „Rückgängig“).
// Story 10.14: EK gestiegen – Marge zu niedrig.
const marginReviewsRoute = createRoute({ getParentRoute: () => rootRoute, path: MARGIN_REVIEWS_PATH, component: lazyRouteComponent(() => import('./pages/margin-reviews.tsx'), 'MarginReviewsPage') });
const supplierPricesRoute = createRoute({ getParentRoute: () => rootRoute, path: SUPPLIER_PRICES_PATH, component: lazyRouteComponent(() => import('./pages/supplier-prices.tsx'), 'SupplierPricesPage') });
const supplierPricesImportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SUPPLIER_PRICES_IMPORT_PATH,
  component: lazyRouteComponent(() => import('./pages/list-imports.tsx'), 'SupplierPricesImportPage'),
});
const supplierPricesMassEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SUPPLIER_PRICES_MASS_EDIT_PATH,
  component: lazyRouteComponent(() => import('./pages/supplier-prices-mass-edit.tsx'), 'SupplierPricesMassEditPage'),
});
const supplierPricesMassEditRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/einkauf/lieferantenpreise/massenbearbeitung/$id',
  component: lazyRouteComponent(() => import('./pages/supplier-prices-mass-edit.tsx'), 'SupplierPricesMassEditRunPage'),
});
// Story 10.15 (10.1b): Massenbearbeitung der Lieferantenliste und ihre Läufe.
const suppliersMassEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SUPPLIERS_MASS_EDIT_PATH,
  component: lazyRouteComponent(() => import('./pages/suppliers-mass-edit.tsx'), 'SuppliersMassEditPage'),
});
const suppliersMassEditRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/einkauf/lieferanten/massenbearbeitung/$id',
  component: lazyRouteComponent(() => import('./pages/suppliers-mass-edit.tsx'), 'SuppliersMassEditRunPage'),
});
const reportsRoute = createRoute({ getParentRoute: () => rootRoute, path: REPORTS_PATH, component: lazyRouteComponent(() => import('./pages/reports.tsx'), 'ReportsPage') });
const dailyRevenueRoute = createRoute({ getParentRoute: () => rootRoute, path: DAILY_REVENUE_PATH, component: lazyRouteComponent(() => import('./pages/reports.tsx'), 'DailyRevenuePage') });
const monthlyRevenueRoute = createRoute({ getParentRoute: () => rootRoute, path: MONTHLY_REVENUE_PATH, component: lazyRouteComponent(() => import('./pages/reports.tsx'), 'MonthlyRevenuePage') });
const channelRevenueRoute = createRoute({ getParentRoute: () => rootRoute, path: CHANNEL_REVENUE_PATH, component: lazyRouteComponent(() => import('./pages/reports.tsx'), 'ChannelRevenuePage') });
const soldPositionsRoute = createRoute({ getParentRoute: () => rootRoute, path: SOLD_POSITIONS_PATH, component: lazyRouteComponent(() => import('./pages/sold-positions.tsx'), 'SoldPositionsPage') });
// Story 18.27: Berichte › Zahlungseingänge (nur Admin; Nicht-Admins sehen „Kein Zugriff“).
const incomingPaymentsRoute = createRoute({ getParentRoute: () => rootRoute, path: INCOMING_PAYMENTS_PATH, component: lazyRouteComponent(() => import('./pages/incoming-payments.tsx'), 'IncomingPaymentsPage') });
// Fragen an die Daten (Story 18.21), nachgeladen.
const dataQuestionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: DATA_QUESTIONS_PATH,
  // Redesign Runde 2: `?text=` = Frage aus dem Frage-Feld der Berichte-Übersicht (nur vorbefüllt, nicht abgeschickt).
  validateSearch: (search: Record<string, unknown>): { text?: string } => (typeof search.text === 'string' && search.text.trim() ? { text: search.text.slice(0, 500) } : {}),
  component: lazyRouteComponent(() => import('./pages/data-questions.tsx'), 'DataQuestionsPage'),
});
const profileRoute = createRoute({ getParentRoute: () => rootRoute, path: '/profil', component: lazyRouteComponent(() => import('./pages/profile.tsx'), 'ProfilePage') });
// Automatisierung: Regeln und Protokoll (epic-regel-baukasten Story 1).
const automationRoute = createRoute({ getParentRoute: () => rootRoute, path: AUTOMATION_PATH, component: lazyRouteComponent(() => import('./pages/rules.tsx'), 'RulesPage') });
// Versand (Story 11.1): Liste der versandbereiten Aufträge und Scan-Maske (Vollbild-Arbeitsmodus).
// Ticket 7.43/1.302: `?auftrag=202725009` (Hauptknopf „Im Versandzentrum öffnen“ am Auftrag) hebt diesen Auftrag in der vollen Liste hervor.
const shippingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/versand',
  component: lazyRouteComponent(() => import('./pages/shipping.tsx'), 'ShippingPage'),
  // Story 11.32/11.33: `?lieferschein=offen` bzw. `=ueberfaellig` zeigt nur Aufträge mit ungedrucktem Lieferschein; andere Werte entfallen.
  // `?teil=2` blättert in der Filter-Ansicht (je 10 Aufträge); nur mit Filter, ungültig → entfällt (Teil 1).
  validateSearch: (search: Record<string, unknown>): { auftrag?: string; lieferschein?: DeliveryNoteFilter; teil?: number } => {
    const value = typeof search.auftrag === 'number' ? String(search.auftrag) : search.auftrag;
    const filter = DELIVERY_NOTE_FILTERS.find((f) => f === search.lieferschein);
    const teil = filter ? parseDeliveryNotePart(search.teil) : undefined;
    return {
      ...(typeof value === 'string' && value.trim() !== '' ? { auftrag: value.trim() } : {}),
      ...(filter ? { lieferschein: filter } : {}),
      ...(teil !== undefined && teil > 1 ? { teil } : {}),
    };
  },
});
const shippingScanRoute = createRoute({ getParentRoute: () => rootRoute, path: '/versand/scan', component: lazyRouteComponent(() => import('./pages/shipping-scan.tsx'), 'ShippingScanPage') });
// Finanzen (Ticket 1.250, Entwürfe G01–G05): Übersicht und vier Bereiche, nur Oberfläche ohne Server-Daten.
const financeRoute = createRoute({ getParentRoute: () => rootRoute, path: FINANCE_PATH, component: lazyRouteComponent(() => import('./pages/finance.tsx'), 'FinancePage') });
// Story 13.8: `?ansicht=liste` öffnet „Braucht dich“ gleich als Liste (Listen-Baukasten, `payment_suggestions`).
const financePaymentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: FINANCE_PAYMENTS_PATH,
  validateSearch: (search: Record<string, unknown>): { ansicht?: 'liste' } => ({ ...(search.ansicht === 'liste' ? { ansicht: 'liste' as const } : {}) }),
  component: lazyRouteComponent(() => import('./pages/finance-payments.tsx'), 'FinancePaymentsPage'),
});
const financeDocumentsRoute = createRoute({ getParentRoute: () => rootRoute, path: FINANCE_DOCUMENTS_PATH, component: lazyRouteComponent(() => import('./pages/finance.tsx'), 'FinanceDocumentsPage') });
const financeOpenItemsRoute = createRoute({ getParentRoute: () => rootRoute, path: FINANCE_OPEN_ITEMS_PATH, component: lazyRouteComponent(() => import('./pages/finance.tsx'), 'FinanceOpenItemsPage') });
// Story 16.9: `?stapel=<id>` (Klärfall „DATEV … nicht gesendet“, Glocke) öffnet das Panel dieses Stapels.
const financeDatevRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: FINANCE_DATEV_PATH,
  component: lazyRouteComponent(() => import('./pages/finance-datev.tsx'), 'FinanceDatevPage'),
  // Bereich „Anzahlungen ohne Rechnung“ (epic-datev-export 16.10): `?bereich=anzahlungen`, Monat `?monat=JJJJ-MM`.
  validateSearch: (search: Record<string, unknown>): { stapel?: string; bereich?: 'anzahlungen'; monat?: string } => ({
    ...(typeof search.stapel === 'string' && /^[0-9a-f-]{36}$/i.test(search.stapel) ? { stapel: search.stapel } : {}),
    ...(search.bereich === 'anzahlungen' ? { bereich: 'anzahlungen' as const } : {}),
    ...(typeof search.monat === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(search.monat) ? { monat: search.monat } : {}),
  }),
});
// Story 16.9: `/finanzen/datev/<Stapel>` (Glocke, Klärfall – Hinweis-Pfade erlauben kein `?`) → Panel dieses Stapels.
const financeDatevBatchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `${FINANCE_DATEV_PATH}/$stapel`,
  beforeLoad: ({ params }) => {
    throw redirect({ to: FINANCE_DATEV_PATH, search: /^[0-9a-f-]{36}$/i.test(params.stapel) ? { stapel: params.stapel } : {}, replace: true });
  },
});
const shippingShipmentsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/versand/sendungen', component: lazyRouteComponent(() => import('./pages/shipping-shipments.tsx'), 'ShippingShipmentsPage') });
const shippingPickupRoute = createRoute({ getParentRoute: () => rootRoute, path: '/versand/abholung', component: lazyRouteComponent(() => import('./pages/shipping-pickup.tsx'), 'ShippingPickupPage') });
// Story 22.1: Zeiterfassung › Heute und Einstellungen › Zeiterfassung › Mitarbeiter.
// Story 22.5: `?nachtragen=<Tag>` (Textlink im Hinweis der Startseite) öffnet das Formular „Nachtragen“ für diesen Tag;
// Zeiterfassung › Nachträge nur für Admins.
const timeTrackingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: TIME_TRACKING_PATH,
  component: lazyRouteComponent(() => import('./pages/time-tracking.tsx'), 'TimeTrackingPage'),
  validateSearch: (search: Record<string, unknown>): { nachtragen?: string } =>
    typeof search.nachtragen === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(search.nachtragen) ? { nachtragen: search.nachtragen } : {},
});
// Story 22.19: Zeiterfassung › Monatsübersicht (nur Admin).
const monthCloseRoute = createRoute({ getParentRoute: () => rootRoute, path: MONTH_CLOSE_PATH, component: lazyRouteComponent(() => import('./pages/staff-month-close.tsx'), 'StaffMonthClosePage') });
const timeAmendmentsRoute = createRoute({ getParentRoute: () => rootRoute, path: TIME_AMENDMENTS_PATH, component: lazyRouteComponent(() => import('./pages/staff-amendments.tsx'), 'StaffAmendmentsPage') });
/** Ticket 22.28: `?monat=YYYY-MM` (01–12) übernehmen, sonst weglassen (Seite nimmt dann den aktuellen Monat bzw. den des Tages). */
const monthSearch = (search: Record<string, unknown>): { monat?: string } => (isMonthKey(search.monat) ? { monat: search.monat } : {});
// Story 22.4: Reiter Meine Zeiten, Team und Arbeitszeitgesetz als Unterrouten.
// Ticket 22.28: Monat als `?monat=YYYY-MM` in der Adresse (Zurück geht); ungültig → aktueller Monat bzw. Monat des Tages.
const myTimesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: MY_TIMES_PATH,
  component: lazyRouteComponent(() => import('./pages/time-tracking-balance.tsx'), 'MyTimesPage'),
  validateSearch: monthSearch,
});
const staffTeamRoute = createRoute({ getParentRoute: () => rootRoute, path: STAFF_TEAM_PATH, component: lazyRouteComponent(() => import('./pages/time-tracking-balance.tsx'), 'StaffTeamPage') });
// Story 22.26: Sprung aus der Glocke zum Tag (Monat gewählt, Tag-Zeile markiert); gleiche Seiten.
const myTimesDayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `${MY_TIMES_PATH}/$tag`,
  component: lazyRouteComponent(() => import('./pages/time-tracking-balance.tsx'), 'MyTimesPage'),
  validateSearch: monthSearch,
});
const staffTeamDayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `${STAFF_TEAM_PATH}/$mitarbeiter/$tag`,
  component: lazyRouteComponent(() => import('./pages/time-tracking-balance.tsx'), 'StaffTeamPage'),
});
const workingTimeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: WORKING_TIME_PATH,
  component: lazyRouteComponent(() => import('./pages/time-tracking-balance.tsx'), 'WorkingTimePage'),
  validateSearch: monthSearch,
});
// Story 22.6: Reiter Urlaub & Abwesenheit und Genehmigen.
const leaveRoute = createRoute({ getParentRoute: () => rootRoute, path: LEAVE_PATH, component: lazyRouteComponent(() => import('./pages/time-tracking-leave.tsx'), 'LeavePage') });
// Story 22.8: Reiter Kalender (Ansicht und Monat in der URL-Suche, damit Links und Zurück funktionieren).
const calendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CALENDAR_PATH,
  component: lazyRouteComponent(() => import('./pages/time-tracking-calendar.tsx'), 'CalendarPage'),
  validateSearch: (search: Record<string, unknown>): { ansicht?: 'team'; monat?: string } => ({
    ...(search.ansicht === 'team' ? { ansicht: 'team' as const } : {}),
    ...(typeof search.monat === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(search.monat) ? { monat: search.monat } : {}),
  }),
});
const approvalsRoute = createRoute({ getParentRoute: () => rootRoute, path: APPROVALS_PATH, component: lazyRouteComponent(() => import('./pages/time-tracking-leave.tsx'), 'ApprovalsPage') });
const staffEmployeesRoute = createRoute({ getParentRoute: () => rootRoute, path: STAFF_EMPLOYEES_PATH, component: lazyRouteComponent(() => import('./pages/staff-employees.tsx'), 'StaffEmployeesPage') });
// `?neu=1` öffnet die Auswahl „Retoure zu welchem Auftrag?“ („+ Neu › Retoure“).
const returnsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/retouren',
  component: lazyRouteComponent(() => import('./pages/returns.tsx'), 'ReturnsPage'),
  validateSearch: (search: Record<string, unknown>): { neu?: 1 } => (search.neu === 1 || search.neu === '1' ? { neu: 1 as const } : {}),
});
// Story 14.1: Vollansicht der Retoure und „Retoure anlegen“ am versendeten Auftrag (fester Teilpfad nach `/auftraege/$id`).
const returnDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/retouren/$returnId',
  component: lazyRouteComponent(() => import('./pages/return-detail.tsx'), 'ReturnDetailPage'),
});
const returnNewRoute = createRoute({ getParentRoute: () => rootRoute, path: '/auftraege/$id/retoure', component: lazyRouteComponent(() => import('./pages/return-new.tsx'), 'ReturnNewPage') });
const placeholderRoutes = comingSoon.map((item) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: item.path,
    component: () => <PlaceholderPage title={item.label} />,
  }),
);

// Story 24.1: Team-Chat – `/chat` (am PC #allgemein, am Handy die Kanalliste) und `/chat/<Kanal>`; `?nachricht=<id>`
// springt zu einer Nachricht.
const chatRoute = createRoute({ getParentRoute: () => rootRoute, path: CHAT_PATH, component: lazyRouteComponent(() => import('./pages/chat.tsx'), 'ChatPage') });
const chatFileRoute = createRoute({ getParentRoute: () => rootRoute, path: `${CHAT_PATH}/datei/$fileId`, component: lazyRouteComponent(() => import('./pages/chat-file.tsx'), 'ChatFilePage') });
const chatChannelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `${CHAT_PATH}/$channelId`,
  component: lazyRouteComponent(() => import('./pages/chat.tsx'), 'ChatChannelPage'),
  validateSearch: (search: Record<string, unknown>): { nachricht?: string } =>
    typeof search.nachricht === 'string' && /^[0-9a-f-]{36}$/i.test(search.nachricht) ? { nachricht: search.nachricht } : {},
});
const chatMessageActionsProbeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/__ci/chat/$channelId', component: lazyRouteComponent(() => import('./pages/chat-message-actions-probe.tsx'), 'ChatMessageActionsProbe') });
const routeTree = rootRoute.addChildren([
  chatMessageActionsProbeRoute,
  chatRoute,
  chatChannelRoute,
  chatFileRoute,
  massEditRedirectRoute,
  customerAddressRedirectRoute,
  watchHitRedirectRoute,
  releaseNotesRoute,
  startRoute,
  tasksRoute,
  taskRoute,
  taskProjectRoute,
  tasksMassEditRoute,
  tasksMassEditRunRoute,
  caseRoute,
  suggestionsRoute,
  suggestionRoute,
  plansRoute,
  planRoute,
  settingsRoute,
  settingsOverviewRoute,
  counterpartsRoute,
  exampleInvoiceRoute,
  switchesRoute,
  exchangeRatesRoute,
  trialRoute,
  comparisonReportRoute,
  cutoverRoute,
  aiRoute,
  aiAgentsRoute,
  aiBudgetRoute,
  aiArticleAssistantRoute,
  mcpKeysRoute,
  credentialsRoute,
  taxRoute,
  mailTemplatesRoute,
  taskLabelsRoute,
  taskTemplatesRoute,
  taskReminderRoute,
  taskRecurrenceRoute,
  labelsRoute,
  labelPrintersRoute,
  labelPrintsRoute,
  labelEditorRoute,
  operationsRoute,
  archiveBackupRoute,
  shippingCostsRoute,
  gtinSettingsRoute,
  shopwareFieldsRoute,
  shopwareFieldCoverageRoute,
  shopwareFieldCoverageAreaRoute,
  documentTemplatesRoute,
  numberRangesRoute,
  documentRoute,
  mailRoute,
  privacyRoute,
  anonymizationsRoute,
  settingsSectionRoute,
  settingsTabRoute,
  usersRoute,
  userDetailRoute,
  testClockRoute,
  ordersRoute,
  orderDraftsRoute,
  orderBacklogRoute,
  blanketOrdersRoute,
  blanketOrderNewRoute,
  blanketOrderDetailRoute,
  receiptNoticeRoute,
  orderNewRoute,
  orderDetailRoute,
  orderEditRoute,
  quotesRoute,
  quoteNewRoute,
  quoteDetailRoute,
  quoteEditRoute,
  customersRoute,
  customerMassEditRoute,
  customerNewRoute,
  customersAnonymizeRoute,
  customerImportRoute,
  customerMassEditRunRoute,
  customerDetailRoute,
  articlesRoute,
  articleNewRoute,
  articleImportRoute,
  gtinNumbersRoute,
  gtinImportRoute,
  catalogMasterDataRoute,
  createRoute({ getParentRoute: () => rootRoute, path: SHOPWARE_IMPORT_PATH, component: lazyRouteComponent(() => import('./pages/shopware-import.tsx'), 'ShopwareImportPage') }), // Story 5.25
  // Story 5.27: Vorschau eines Laufs (Klick auf die Aufgabe „Übernahme aus Shopware ‚…‘: n Änderungen zur Prüfung“).
  createRoute({ getParentRoute: () => rootRoute, path: `${SHOPWARE_IMPORT_PATH}/lauf/$runId`, component: lazyRouteComponent(() => import('./pages/shopware-import.tsx'), 'ShopwareImportPage') }),
  articleMassEditRoute,
  articleMassEditRunRoute,
  // Ticket 1.204: Testseite der Datensatz-Bausteine (nur Testumgebung, ohne Navigationseintrag).
  createRoute({ getParentRoute: () => rootRoute, path: RECORD_PATTERN_PATH, component: lazyRouteComponent(() => import('./pages/record-pattern.tsx'), 'RecordPatternPage') }),
  // Ticket 1.215: Testseite der Eingabe-Bausteine (nur Testumgebung, ohne Navigationseintrag).
  createRoute({ getParentRoute: () => rootRoute, path: INPUT_PATTERN_PATH, component: lazyRouteComponent(() => import('./pages/input-pattern.tsx'), 'InputPatternPage') }),
  // Ticket 1.241: Testseite der Grundstein-Bausteine und Tokens (nur Testumgebung, ohne Navigationseintrag).
  createRoute({ getParentRoute: () => rootRoute, path: COMPONENT_PATTERN_PATH, component: lazyRouteComponent(() => import('./pages/component-pattern.tsx'), 'ComponentPatternPage') }),
  // Ticket 1.258: Musterseite der kompakten Einstellungen (Baustein C), nur Testumgebung.
  createRoute({ getParentRoute: () => rootRoute, path: SETTINGS_PATTERN_PATH, component: lazyRouteComponent(() => import('./pages/settings-pattern.tsx'), 'SettingsPatternPage') }),
  articleDetailRoute,
  inventoryRoute,
  inventoryMovementsRoute,
  inventoryValuationRoute,
  binLocationsRoute,
  binLocationsMassEditRoute,
  binLocationsMassEditRunRoute,
  inventoryMassEditRoute,
  inventoryMassEditRunRoute,
  inventoryImportRoute,
  stocktakesRoute,
  stocktakeNewRoute,
  stocktakeRoute,
  stocktakePrintRoute,
  recountsRoute,
  purchasingRoute,
  suppliersRoute,
  suppliersImportRoute,
  supplierNewRoute,
  supplierDetailRoute,
  purchaseOrdersRoute,
  reorderProposalsRoute,
  purchaseOrderDetailRoute,
  supplierPricesRoute,
  marginReviewsRoute,
  supplierPricesImportRoute,
  supplierPricesMassEditRoute,
  supplierPricesMassEditRunRoute,
  suppliersMassEditRoute,
  suppliersMassEditRunRoute,
  reportsRoute,
  dailyRevenueRoute,
  monthlyRevenueRoute,
  channelRevenueRoute,
  soldPositionsRoute,
  incomingPaymentsRoute,
  dataQuestionsRoute,
  profileRoute,
  automationRoute,
  shippingRoute,
  shippingScanRoute,
  financeRoute,
  financePaymentsRoute,
  financeDocumentsRoute,
  financeOpenItemsRoute,
  financeDatevRoute,
  financeDatevBatchRoute,
  shippingShipmentsRoute,
  shippingPickupRoute,
  returnsRoute,
  timeTrackingRoute,
  timeAmendmentsRoute,
  monthCloseRoute,
  staffEmployeesRoute,
  myTimesRoute,
  myTimesDayRoute,
  staffTeamRoute,
  staffTeamDayRoute,
  workingTimeRoute,
  leaveRoute,
  calendarRoute,
  approvalsRoute,
  returnDetailRoute,
  returnNewRoute,
  ...placeholderRoutes,
]);

// Zurück aus einem Datensatz an dieselbe Scrollstelle der Liste (Story 1.23, EXPERIENCE §10.2).
export const router = createRouter({ routeTree, scrollRestoration: true });

/**
 * Teil der aufgerufenen Seite gleich beim Start laden (Story 7.21): Jede Seite ist ein eigener Teil des Pakets und käme
 * sonst erst nach der Anmeldung (`/me`); so lädt er zugleich mit ihr. Fehler meldet die Seite selbst beim Anzeigen.
 */
export function preloadPage(pathname: string): void {
  const [routes] = router.getMatchedRoutes(pathname);
  for (const route of routes) void router.loadRouteChunk(route)?.catch(() => {});
}

/** Liste der aufgerufenen Seite (`staticData.list`), deren Ansichten und Spalten der Start vorab lädt; sonst `undefined`. */
export function pageList(pathname: string): string | undefined {
  const [, , found] = router.getMatchedRoutes(pathname);
  return found?.options.staticData?.list;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  interface StaticDataRouteOption {
    /** Große Liste der Seite (Listen-Baukasten): Ansichten und Spalten lädt der Start zugleich mit der Anmeldung. */
    list?: string;
  }
}
